<template>
    <div class="flex flex-col gap-4 items-center justify-center">
        <template v-if="ARLink">
            <client-only>
                <template v-if="isDesktop">
                    <a
                        :href="arIntent || undefined"
                        rel="ar"
                    >
                        <VueQRCodeComponent
                            v-if="ARLink"
                            :text="ARLink"
                            class="w-48"
                        />
                        <img>
                    </a>

                    <p>
                        Scannen Sie den Code mit Ihrem Handy und platzieren Sie das Spielgerät in Ihrer
                        Umgebung.
                    </p>
                    <p>
                        Falls Sie diese Seite bereits mit einem AR-fähigen Tablet anschauen, tippen Sie einfach auf
                        den QR-Code.
                    </p>
                </template>

                <template v-else>
                    <a
                        class="bordered w-1/2 m-4 button"
                        :href="arIntent || undefined"
                        rel="ar"
                    >
                        AR-Ansicht aufrufen
                        <img>
                    </a>
                </template>
            </client-only>
        </template>

        <template v-else>
            <p>Leider konnte kein AR-Modell gefunden werden. Bitte versuchen Sie es mit einem anderen Produkt.</p>
        </template>

        <div class="space-y-8">
            <p>
                <span class="font-bold">Für mehr Funktionen</span> und vollumfänglichen Service laden Sie sich auch
                gleich unsere 3D-Planer-App herunter:
            </p>
            <div class="flex justify-center gap-8">
                <NuxtLink
                    href="https://apps.apple.com/de/app/3d-planer/id1593852740"
                    target="_blank"
                >
                    <NuxtPicture
                        src="/images/icons/icon-app-store.svg"
                        alt="App Store"
                        class="h-12"
                    />
                </NuxtLink>
                <NuxtLink
                    href="https://play.google.com/store/apps/details?id=com.Convario.SauerlandAR"
                    target="_blank"
                    class="place-self-end"
                >
                    <NuxtPicture
                        src="/images/icons/icon-google-play.svg"
                        alt="Google Play"
                        class="h-12"
                    />
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import VueQRCodeComponent from 'vue-qrcode-component-v3';
import {useCombinationModelStore} from '~~/stores/combinationmodel';

type ArticleLike = {
    configuratorId?: string | number | null
    articleNumber?: string | null
}

const props = defineProps<{ article: ArticleLike }>();

const {public: cfg} = useRuntimeConfig();
// WICHTIG: setze frontendURL in runtimeConfig.public.frontendURL
// alternativ: cfg.siteUrl o.ä. verwenden
const frontendURL = computed(() => cfg.frontendURL || cfg.siteUrl || '');

const store = useCombinationModelStore();
const hasCombinationModel = ref<string | false>(false);

/**
 * Simple Device Detection (ohne extra Module)
 * (ClientOnly sorgt dafür, dass navigator nur im Browser benutzt wird)
 */
const ua = computed(() => (import.meta.client ? navigator.userAgent : ''));
const isAndroid = computed(() => /Android/i.test(ua.value));
const isIOS = computed(() => /iPhone|iPad|iPod/i.test(ua.value));
const isMacOS = computed(() => /Macintosh/i.test(ua.value) && !isIOS.value);

// Desktop-Logik wie früher $device.isDesktop
const isDesktop = computed(() => !isAndroid.value && !isIOS.value);

onMounted(async () => {
    // wie früher: nur prüfen, wenn kein configuratorId
    if (!props.article?.configuratorId) {
        hasCombinationModel.value = await store.check(props.article);
    }
});

const ARLink = computed(() => {
    if (!frontendURL.value) return null;
    if (props.article?.configuratorId) {
        return `${frontendURL.value}/ar/${props.article.configuratorId}`;
    }
    if (hasCombinationModel.value) {
        return `${frontendURL.value}/ar/${hasCombinationModel.value}/combo`;
    }
    return null;
});

const arIntent = computed<string | false>(() => {
    if (!import.meta.client) return false;

    let urlPart: string | undefined;

    if (props.article?.configuratorId) {
        urlPart = `articles/${props.article.configuratorId}`;
    } else if (hasCombinationModel.value) {
        urlPart = `combinations/${hasCombinationModel.value}`;
    }

    if (!urlPart) return false;

    const {origin} = window.location;

    // iOS / macOS QuickLook (USZ)
    if (isMacOS.value || isIOS.value) {
        return `${origin}/ar-files/${urlPart}/scene_ar.usdz`;
    }

    // Android Scene Viewer
    if (isAndroid.value) {
        const fileUrl = `${origin}/ar-files/${urlPart}/scene_ar.gltf`;
        return `intent://arvr.google.com/scene-viewer/1.1?file=${fileUrl}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end;`;
    }

    return false;
});

function showAR() {
    const intent = arIntent.value;
    if (!intent || !import.meta.client) return;

    const anchor = document.createElement('a');
    anchor.setAttribute('rel', 'ar');
    anchor.appendChild(document.createElement('img'));
    anchor.setAttribute('href', intent);
    anchor.click();
}
</script>
